import React from 'react'
import ReactDOM from 'react-dom'
import Close from '../icons/Close'
import styles from './Modal.module.css'

interface Modal {
  active: boolean
  onCancel?: () => void
  onConfirm?: () => void
  maxWidth?: string
  title?: string
  children?: React.ReactNode
  height?: string
  modalBodyPadding?: string
}

function Modal({
  active,
  onCancel,
  onConfirm,
  maxWidth = 'none',
  title,
  children,
  height,
  modalBodyPadding,
}: Modal) {
  const [container, setContainer] = React.useState<HTMLDivElement | null>(null)
  const [content, setContent] = React.useState<HTMLDivElement | null>(null)
  const [cancelButton, setCancelButton] = React.useState<HTMLButtonElement | null>(
    null,
  )
  const [modal, setModal] = React.useState<HTMLDivElement | null>(null)

  React.useEffect(() => {
    const div = document.createElement('div')
    setModal(div)
  }, [])

  React.useEffect(() => {
    if (typeof document === 'object' && modal) {
      document.body.appendChild(modal)
    }
    return function cleanup() {
      if (typeof document === 'object' && modal) {
        document.body.removeChild(modal)
      }
    }
  }, [modal])

  React.useEffect(() => {
    if (cancelButton !== null) {
      cancelButton.focus()
    }
  }, [cancelButton])

  const containerRef = React.useCallback((node) => {
    if (node !== null) {
      setContainer(node)
    }
  }, [])
  const contentRef = React.useCallback((node) => {
    if (node !== null) {
      setContent(node)
    }
  }, [])

  const cancelRef = React.useCallback((node) => {
    if (node !== null) {
      setCancelButton(node)
    }
  }, [])

  function onClick(e: React.MouseEvent) {
    e.stopPropagation()
    if (
      (e.target !== cancelButton && e.target instanceof Node &&
        !cancelButton?.contains(e.target) &&
        content?.contains(e.target)) ||
      e.target === content
    ) {
      return
    }
    e.preventDefault()
    if (onCancel && typeof onCancel === 'function') {
      onCancel()
    }
  }
  function onBlur(e: React.FocusEvent<Element>) {
    if (container === null) {
      return
    }
    if (
      onCancel &&
      typeof onCancel === 'function' &&
      !container.contains(e.relatedTarget)
    ) {
      cancelButton?.focus()
    }
  }
  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape' && onCancel && typeof onCancel === 'function') {
      onCancel()
    } else if (e.target === cancelButton && e.key === 'Enter') {
      e.preventDefault()
      if (onConfirm && typeof onConfirm === 'function') {
        onConfirm()
      }
    }
  }

  return modal
    ? ReactDOM.createPortal(
      !active ? null : (
        <div
          className={styles.modal}
          onClick={onCancel && typeof onCancel === 'function' ? onClick : () => null}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          ref={containerRef}
        >
          <div
            className={styles.modalWrapper}
            style={{
              maxWidth: maxWidth,
            }}
            ref={contentRef}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>{title}</h2>
              {onCancel && typeof onCancel === "function" && (
                <button className={styles.closeButton} onClick={(e) => { e.preventDefault(); onCancel() }} ref={cancelRef}>
                  <Close className={styles.closeIcon} color="var(--red)" />
                </button>
              )}
            </div>
            <div className={styles.modalBody} style={{height: height, padding: modalBodyPadding}}>
              {children}
            </div>
          </div>
        </div>
      ),
      modal,
    )
    : null
}

export default Modal
